import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { updateBookingReference } from "api";
import Button from "components/v2/Button/Button";
import Input from "components/v2/Input/Input";
import Modal from "components/v2/Modal/Modal";
import errorToast from "helpers/errorToast";

type EditBookingReferenceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  shipmentId: string;
  currentValue: string;
  label: string;
  onSuccess: () => void;
};

type FormValues = {
  bookingReference: string;
};

const EditBookingReferenceModal: FC<EditBookingReferenceModalProps> = function ({
  isOpen,
  onClose,
  shipmentId,
  currentValue,
  label,
  onSuccess,
}) {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const methods = useForm<FormValues>({ defaultValues: { bookingReference: currentValue } });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (isOpen) reset({ bookingReference: currentValue });
  }, [isOpen, currentValue]);

  const handleClose = () => {
    onClose();
  };

  const isAddMode = !currentValue;

  const handleSave = handleSubmit(({ bookingReference }) => {
    setIsLoading(true);
    updateBookingReference(shipmentId, { booking_reference_value: bookingReference })
      .then(() => {
        onSuccess();
        onClose();
      })
      .catch((err: any) => errorToast(err))
      .finally(() => setIsLoading(false));
  });

  return (
    <Modal show={isOpen} onHide={handleClose} size="md">
      <Modal.Header>
        <div className="text-primary text-xl font-semibold">
          {intl.formatMessage({
            id: isAddMode ? "tracking.addBookingReference" : "tracking.editBookingReference",
            defaultMessage: isAddMode ? "Add booking reference" : "Edit booking reference",
          })}
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="mt-2xl">
          <FormProvider {...methods}>
            <Input
              label={label}
              name="bookingReference"
            />
          </FormProvider>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              variant="tertiary-color"
              label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
              onClick={handleClose}
            />
          </div>
          <Button
            label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
            onClick={handleSave}
            disabled={isLoading}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditBookingReferenceModal;
